import { action, computed, flow, observable, makeObservable, runInAction } from "mobx"
import { Observe } from "store/page/mixin/ObserveMixin"
import { Timeout } from "store/util/Timeout"
import { PageAbstract } from "./PageAbstract"
import { MinuteInMs, moment } from "store/util/moment"
import { EventEmitter } from "components/utils/EventEmitter"
import { rootStore } from "store/RootStore"
import { timeNow } from "utils/Utils"
import { serviceUrls } from "store/qlapi/ServiceUrls"
import { appFeatures } from "AppFeatures"
import { eventId, eventType, startTs, endTs } from "utils/EventHelpers"
import { EventDetailsMode } from "@quickline/coco"
import { analyticsScreen, analyticsMeta, analyticsProgramDetailsSource } from "store/qlapi/LogService"

const oEpgTabs = {
	CURR: "epgCurr",
	PREV: "epgPrev",
	NEXT: "epgNext"
}

// MTV-2133: NO EPG TABS ANYMORE (disabled some time ago)
//const tEpgTabs = Object.values(oEpgTabs)

export class LiveTv extends PageAbstract {
	// MTVW-246
	//epgCurr = this._root.api.GetChannelsWithEvents({ _tags: ["LiveTvEpg"], _idCache: oEpgTabs.CURR }).fillData()
	epgNow = null
	/* NO EPG TABS ANYMORE
	@observable.shallow epgPrev = this._root.api.GetChannelsWithEvents({ _tags: ["LiveTvEpg"], _idCache: oEpgTabs.PREV, bPrev: true })
	@observable.shallow epgNext = this._root.api.GetChannelsWithEvents({ _tags: ["LiveTvEpg"], _idCache: oEpgTabs.NEXT, bNext: true })
	*/

	epgTabName = oEpgTabs.CURR

	scrollTop = 0

	_refElemScroll = null

	_epgWatchTimer = new Timeout(this, "_epgWatchTimer")

	_observer = new Observe(this, "_observer")

	_bFetchElseData = false

	_autoStartDone = false
	_handlePlayEventAsyncInProgress = false
	_currentChannel = null
	_currentId = null
	// MTV-3012
	memoSelectedEpg = null	// captured EPG of active channel for synchronization with end of playback

	isTextExpanded = false
	eventDetailsActive = null
	playStarted = false

	constructor(parent, path) {
		super(parent, path)
		makeObservable(this, {
			// MTVW-246
			//epgCurr: observable.shallow,
			epgNow: observable.shallow,
			epgTabName: observable,
			scrollTop: observable,
			isTextExpanded: observable,
			// MTV-3583: do not make it computed here, mobx 6 generates an error if it's already registered in the base class
			//isReady: computed,
			epgArray: computed,
			isReplayDone: computed,
			dependenciesLoaded: computed,
			setEpgTabName: action,
			handlePlayChannelAsync: action,
			_handlePlayEventAsync: action,
			_fetchElseData: action,
			setEpgListPos: action,
			setVerticalScroll: action,
			setTextExpanded: action,
			setScrollTop: action,
			reloadChannelEpg: action,
			getProgramsForNow: action
		})

		EventEmitter.subscribe("inactive", (event) => { this._isInactive(event) })
		EventEmitter.subscribe("active", (event) => { this._isActive(event) })
	}

	initAsync = flow(function* () {
		// NOTE: gets called at startup
		// MTVW-246
		// MTVW-920: commented, limit number of calls, channelsMS generates "unsubscribed" channels if multiple simulaneous requests are isseud
		// now loaded in AppFeatures.checkTraxisOnStartup
		//yield rootStore.channelsService.getChannelsAsync()
		rootStore.channelsService.setActiveChannel(rootStore.channelsService.getFirstChannel ? rootStore.channelsService.getFirstChannel.id : null)
		// MTVW-536, MTVW-584: commented to mitigate epg failures
		//yield this.getProgramsForNow()
		//console.debug("CHANNELS %o", channels)
		// MTVW-966: now done in _onMount
		//yield this.reloadChannelEpg(!this.isVisible)
		this._setObserve()
		// MTVW-831: "?mode=box&w=" + 44 * 4 -> "?mode=box&w=480&h=270"
		rootStore.channelsService.preloadChannelPictures("?mode=box&w=480&h=270")
	})

	_onMount() {
		console.debug("mutex info _onMount LiveTv")
		this.setEpgTabName(oEpgTabs.CURR)
		this._autoStartDone = false
		//this._setMounted(true)
		this.playStarted = false
		this._bFetchElseData = false
		this.epgNow = null
		this._handlePlayEventAsyncInProgress = false
		this.reloadChannelEpg(true).then((res) => {
		})
		.finally(() => {
			this._setMounted(true)
			rootStore.page.Discover.acquireMutex("playerLiveTvVo")
		})
	}

	_onUnmount() {
		console.debug("mutex info _onUnmount LiveTv")
		rootStore.page.Discover.releaseMutex("playerLiveTvVo")
		rootStore.page.Player.playerLiveTvVo.setRefElemContainer(null, true).then(() => {
			//rootStore.page.Discover.releaseMutex("playerLiveTvVo")
		})
	}

	get player() {
		return this._root.page.Player
	}

	get isReady() {
		// MTVW-246
		return appFeatures.traxisAvailable ? this.epgNow && rootStore?.sso?.profile?.loginResult && rootStore.page.MsEpg.isReady : this.epgNow && serviceUrls.login && rootStore.page.MsEpg.isReady
	}

	getChannelEpg(id) {
		//const result = this.epgNow ? this.epgNow.find(i => i.channelId === id) : null
		const result = (this.epgNow && typeof this.epgNow.find === "function") ? this.epgNow.find(i => i.channelId === id) : null
		//if (result) result.Events[0].Title = { SeasonNum: "1", EpisodeNum: "2", EpisodeName: "Unhail" }
		// for compatibility with traxis model
		return result ? { Channel: rootStore.channelsService.getChannel(id), Event: result?.Events?.[0] ?? result?.events?.[0]} : null
	}

	getChannelsEpg(channels) {
		//console.debug("getChannelsEpg", channels.length)
		return channels?.map(ch => this.getChannelEpg(ch.id)).filter(i => i && i.Event)
	}

	get dependenciesLoaded() {
		//console.debug("dependenciesLoaded", this.epgArray.length, rootStore.page.Discover.discoverAndLiveMutex.owner === "playerLiveTvVo")
		return this.isVisible && this.epgArray?.length > 0 && rootStore.page.Discover.discoverAndLiveMutex.owner === "playerLiveTvVo"
	}

	get epgArray() {
		// MTVW-246, MTVW-1013
		return this.isReady ? this.getChannelsEpg(rootStore.channelsService.getChannelsFromList(rootStore.channelsService.channelList)) : []
	}

	get isReplayDone() {
		//console.debug(this.player.source?.type)
		if (this.isReady && (this.player.source?.type === "ChannelReplay")) {
			//console.debug("CHANNEL %s, ID %", this.player.source?.id, this.player.source?.Event?.id)
			if ((this._currentChannel === this.player.source?.id) && (this._currentId !== this.player.source?.Event?.id)) {
				//this._currentChannel = null
				//this._currentId = null
				return true
			}
			this._currentChannel = this.player.source?.id
			this._currentId = this.player.source?.Event?.id
		}
		return false
	}

	get _getAvailabilityEndMinDate() {
		const SecondInMs = 1000
		if (!this.isReady) {
			return SecondInMs
		}
		let timeout = Math.floor(
			Math.min.apply(
				Math,
				// MTVW-246, MTVW-1013
				this.getChannelsEpg(rootStore.channelsService.getChannelsFromList(rootStore.channelsService.channelList)).map(i => endTs(i.Event.event))
			)) - moment().utc().valueOf()
		if (timeout === Infinity) timeout = SecondInMs
		// MTVW-874: EPG can be down or channels are empty, above expression will return undefined in this case
		// TODO: do we need commented condition?
		if (isNaN(timeout) /*|| timeout <= 0*/) timeout = null
		console.debug("_getAvailabilityEndMinDate", timeout)
		return timeout
	}

	_setObserve() {
		/* MTVW-1091: commented
		this._observer.add("ChannelListRepoChannelsFromList.ChannelsFromList", this._root.page.ChannelListRepo, "ChannelsFromList", changes => {
			//console.debug("_observer ChannelsFromList %o", changes)
			this.reloadChannelEpg(true)
		})
		*/
		this._observer.add("this.dependenciesLoaded", this, "dependenciesLoaded", changes => {
			console.debug("_observer dependenciesLoaded %s", changes.newValue, this.player.source.type, this._autoStartDone, this.player, rootStore.channelsService.getFirstChannel)
			if (changes.newValue === true && this._bFetchElseData) {
				this._fetchElseData()
			}
			if (changes.newValue === true) {
				// MTVW-217: restore Live TV state if necessary - DISABLED in PlayerVo
				//console.debug("Back in Live TV: player active %s, pause state %s, inactive reason %s, inactive channel %s", this.player.player.isPlayerActive, this.player.player.activePauseState, this.player.player.inactiveReason, this.player.player.inactiveChannelId)
				if (this.player.player.inactiveReason === "reasonStopped" && this.player.player.inactiveChannelId === rootStore.channelsService.activeChannelId) {
					// restore state
					this.player.player._isActive()
				}
				else {
					// regular case
					//console.debug("live tv regular")
					// MTVW-727b: could be an option to go back to live TV without starting new stream
					//if (!this.player.player.isInPip) this.handlePlayChannelAsync()
					//console.debug("calling handlePlayChannelAsync _setObserve")
					if (!rootStore.channelsService.activeChannelId) rootStore.channelsService.setActiveChannel(rootStore.channelsService.getFirstChannel ? rootStore.channelsService.getFirstChannel.id : null)
					console.debug("calling handlePlayChannelAsync observer", rootStore.channelsService.getFirstChannel, rootStore.channelsService.activeChannelId)
					this.handlePlayChannelAsync()
				}
			}
		})
		this._observer.add("this.isReplayDone", this, "isReplayDone", changes => {
			if (changes.newValue === true) {
				//console.debug("switching to current tab")
				this.setEpgTabName(oEpgTabs.CURR)
			}
		})
	}

	_isInactive(event) {
		//console.debug("LiveTv inactive")
		// nothing to do yet
	}

	_isActive(event) {
		//console.debug("LiveTv active")
		// reload of EPG done in PlayerAbstract
	}

	getProgramsForNow = flow(function* () {
    // MTVW=1075: get progs from CoCo
		let progs = this.epgNow
		try {
			progs = yield rootStore.page.MsEpg.getProgramsForNow()
		}
		catch (e) {
			// ignore
			// MTVW-1075, item 2: CoCo throttled the call sometimes, not reproducible on my machine
			console.error("getProgramsForNow LiveTv", e)
			return
		}
		if (progs?.length > 0) this.epgNow = progs
	})

	reloadChannelEpg = flow(function* (bCheckOnly = false) {
		console.info("@@reloadChannelEpg(bCheckOnly=%o)", bCheckOnly)
		if (!rootStore.sso.profile?.loginResult) {
			//console.debug("serviceUrls.login", serviceUrls.login)
			this._epgWatchTimer.setTimeout(() => this.reloadChannelEpg(), 1000)
			return
		}
		//else yield rootStore.channelsService.getChannelsAsync()
		console.debug("%s: RELOAD reloadChannelEpg", timeNow(), bCheckOnly, serviceUrls.login)
		if (!rootStore.channelsService.activeChannelId) rootStore.channelsService.setActiveChannel(rootStore.channelsService.getFirstChannel ? rootStore.channelsService.getFirstChannel.id : null)

		if (bCheckOnly !== true) {
			// always preload last used tab
			// MTVW-246
			//yield this[this.epgTabName].fetchDataAsync()
			yield this.getProgramsForNow()
			// MTV-1770, MTV-1771, MTV-1772: call handlePlayChannelAsync only 1 time after _onMount
			if (this.isVisible && !this._autoStartDone) {
				//console.debug("reloadChannelEpg: calling handlePlayChannelAsync", this.player.source.type)
				console.debug("calling handlePlayChannelAsync reloadChannelEpg")
				yield this.handlePlayChannelAsync()
				// MTV-3012: set _autoStartDone in handlePlayChannelAsync to avoid play when the app is loaded
				// with TV Guide active and then playing a channel in Live TV
				//this._autoStartDone = true
			}


			// current tab next
			if (this.epgTabName !== oEpgTabs.CURR) {
				// MTVW-246
				//yield this.epgCurr.fetchDataAsync()
				yield this.getProgramsForNow()
			}

			// preload else tabs if visible else load later
			if (this.isVisible) {
				yield this._fetchElseData()
			} else {
				this._bFetchElseData = true
			}
		} else {
			// MTVW-246
			//this.epgCurr.fillData()
			yield this.getProgramsForNow()
			/* NO EPG TABS ANYMORE
			this.epgPrev.fillData()
			this.epgNext.fillData()
			*/
		}

		const timeout = this._getAvailabilityEndMinDate
		//console.debug("timeout=", timeout, timeout !== null ? Math.max(1000, timeout * 1000 - this._root.time.getTimeStamp()) + 3000 : MinuteInMs)
		// reload EPG 3s after the calculated time to avoid asynchronity of clocks
		this._epgWatchTimer.setTimeout(() => this.reloadChannelEpg(), timeout !== null ? Math.max(1000, timeout) + 3000 : MinuteInMs)
		// MTVW-246: refresh every 30 seconds
		//this._epgWatchTimer.setTimeout(() => this.reloadChannelEpg(), 30 * 1000)
	})

	isEpgEvent(oEvent) {
		return computed(() => {
			//console.log(oEvent)
			//oEvent.Channel.id === rootStore.channelsService.activeChannelId &&

			//return oEvent.Event.id === this.player.source?.Event?.id

			// make sure the active channel in the EPG is highlighted upon EPG change
			// MTVW-246
			//return oEvent.id === rootStore.channelsService.activeChannelId
			return oEvent?.Channel?.id === rootStore.channelsService.activeChannelId
		}).get()
	}

	setRefElemScroll(refElem) {
		this._refElemScroll = refElem
		if (refElem !== null) {
			this._refElemScroll.scrollTop = this.scrollTop
		}
	}

	setEpgTabName(sTabName) {
		if (this.epgTabName === sTabName && this._refElemScroll !== null) {
			// MTV-1646: do not reset
			//this._refElemScroll.scrollTop = 0
		} else if (this.epgTabName !== sTabName) {
			this.epgTabName = sTabName
			this[this.epgTabName].fillData()
		}
		return this
	}

	handlePlayChannelAsync = flow(function* (channelId = null, useLive = false) {
		console.log("@(channelId=%o)", channelId)
		if (channelId !== null) {
			rootStore.channelsService.setActiveChannel(channelId)
		}
		// MTVW-246
		/*
		yield this[this.epgTabName].waitAsync()
		const currEvent = this[this.epgTabName].Data.getChannelEpg(rootStore.channelsService.activeChannelId)
		*/
		const currEvent = this.getChannelEpg(rootStore.channelsService.activeChannelId)
		const evtId = eventId(currEvent?.Event)
		// MTVW-919: added && evtId
		if (currEvent && evtId) {
			// MTV-1750: set bForce true
			//console.debug("handlePlayChannelAsync channel %o, event %o, source %s, paused %o", rootStore.channelsService.activeChannelId, currEvent, this.player.source.type, this.player.playerLiveTvVo.isPausedUser, this.player.player)
			// MTVW-996: playStarted to prevent double play start when LiveTv starts
			if (!this.playStarted) {
				//yield rootStore.page.Discover.acquireMutex("playerLiveTvVo")
				this.playStarted = true
				try {
					yield this._handlePlayEventAsync(evtId, true, useLive)
				}
				catch (e) {
					console.error("_handlePlayEventAsync", e)
				}
				finally {
					this.playStarted = false
					//rootStore.page.Discover.releaseMutex("playerLiveTvVo")
					// MTV-3012: set _autoStartDone here
					this._autoStartDone = true
				}
			}
			else console.warn("mutex info playStarted")
		}
		else console.debug("no currEvent", currEvent, rootStore.page.MsEpg.isReady)
	})

	setEpgListPos(channelId, height = null) {
		if (height !== null) {
			const index = rootStore.channelsService.indexInChannelList(channelId)
			this.setScrollTop(index > 0 ? index * height : 0)
		}
		this.setVerticalScroll()
	}

	// called from TvGuide when clicked on the channel logo
	handlePlayChannelLive(channelId, height = null) {
		console.log("@(channelId=%o)", channelId)
		// MTV-1646: prevent the player to use the previous channel _onMount
		// MTV-1673: Prevent only if different channel
		if (channelId !== rootStore.channelsService.activeChannelId) {
			this._pageStore.Player._tLastUsage = []
			rootStore.channelsService.setActiveChannel(channelId)
		}
		this.setEpgTabName(oEpgTabs.CURR)
		this.setEpgListPos(channelId, height)
	}

	// MTVW-618: added useLive for handleChangeChannel
	_handlePlayEventAsync = flow(function* (eventId, bForce = null, useLive = false) {
		console.log("@@_handlePlayEventAsync(eventId=%o)", eventId)
		/* MTVW-272
		if (this.eventDetailsActive !== null) {
			this.eventDetailsActive.abortReq()
		}
		*/
		this.eventDetailsActive = null
		if (!eventId) return
		// MTVW-965: prevent multiple executions at startup
		if (this._handlePlayEventAsyncInProgress) return
		this._handlePlayEventAsyncInProgress = true
		try {
			// MTVW=1075: EventDetailsMode.Lite
			this.eventDetailsActive = yield rootStore.page.MsEpg.getEventDetailsAsync(eventId, EventDetailsMode.Lite)

			// MTVW-577: stop keep alive
			//console.debug("_handlePlayEventAsync", this, this._pageStore.Player)
			//this._pageStore.Player.player.setClose()

			//const event = this.eventDetailsActive.Data
			const eventDetails = this.eventDetailsActive
			if (eventDetails) {
				let evtType = eventType(eventDetails.event)
				// MTVW-618: since getCurrentEventType truncates with getTimeStampTick(60) there is a slight chance that calculated
				// event type is in the future, when the show of the channel just changed to the next one.
				// Since this is called only in liveTv we can ignore future events to avoid the popup OverlayEventDetails
				if (evtType === "n" && useLive) {
					//console.debug("_handlePlayEventAsync type %s, now %o, tick %o, tick1 %o, event %o, source %o, player %o", eventType, timeNow(), moment(rootStore.time.getTimeStampTick(60)).local().format("HH:mm:ss.SSS"), moment(rootStore.time.getTimeStampTick(null)).local().format("HH:mm:ss.SSS"), event, this.player.source, this.player)
					evtType = "c"
				}
				if (evtType === "c") {
					// GT12: prevent displaying adSkipInfo when switching from TV Guide
					yield this._pageStore.OverlayEventDetails.setEpgEventById(eventId, false, { [analyticsMeta.screen]: analyticsScreen.programDetails, source: analyticsProgramDetailsSource.liveTv })
					// MTVW-1075 NOTE: if no yield to display event details before player is ready, fast channel switching
					// does not show always recommendations
					yield this._pageStore.Player.setPlayChannelLiveAsync(rootStore.channelsService.activeChannelId, eventDetails, bForce, false, true)
				} else if (evtType === "p") {
					console.debug("_handlePlayEventAsync setPlayChannelReplayAsync")
					yield this._pageStore.Player.setPlayChannelReplayAsync(rootStore.channelsService.activeChannelId, startTs(eventDetails), eventDetails, bForce)
				} else {
					// setShow first so that derived 'origin' is correct
					this._pageStore.OverlayEventDetails.setShow(true)
					yield this._pageStore.OverlayEventDetails.setEpgEventById(eventId, false, { [analyticsMeta.screen]: analyticsScreen.programDetails, source: analyticsProgramDetailsSource.liveTv })
					this._pageStore.OverlayEventDetails.setComponentType('event')
				}
				// MTVW-1075: now load the full event from CoCo
				// MTVW-1096, finding 1: kick off fetching full events without yield to prevent delayed start of playback
				//this.eventDetailsActive = yield rootStore.page.MsEpg.getEventDetailsAsync(eventId, EventDetailsMode.Full)
				//this.player.source.GetEvent = this.eventDetailsActive
				rootStore.page.MsEpg.getEventDetailsAsync(eventId, EventDetailsMode.Full).then(fullEvent => {
					runInAction(() => {
						this.eventDetailsActive = fullEvent
						this.player.source.GetEvent = fullEvent
					})
				})
			}
		} catch (e) {
			// to do
		}
		finally {
			this._handlePlayEventAsyncInProgress = false
		}
	})

	_fetchElseData() {
		this._bFetchElseData = false
		/* NO EPG TABS ANYMORE
		return Promise.all(
			tEpgTabs
				.filter(i => i !== this.epgTabName && i !== oEpgTabs.CURR)
				.map(i => this[i])
				.map(i => i.fetchDataAsync())
		)
		*/
	}

	setVerticalScroll() {
		console.log("@()")
		if (this._refElemScroll !== null) {
			this.setScrollTop(Math.max(0, this._refElemScroll.scrollTop))
		}
		return this
	}

	setTextExpanded(isTextExpanded = null) {
		this.isTextExpanded = isTextExpanded === null ? !this.isTextExpanded : isTextExpanded
		return this
	}
	setScrollTop(value) {
		//if (value === 0) console.trace("setScrollTop", typeof value, value)
		if (value >= 0) this.scrollTop = value
		return this
	}
}
